<template>
  <div class="kv-ve__group">
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-container-name") }}</label>
      <input class="form-control" v-model="nameProxy" />
    </div>

    <div class="form-group">
      <label>{{ $_('visualEditor-emailEditor-common-padding') }}</label>
      <VeSpacingPicker
        v-model="paddingProxy"
        :y-label="$_('visualEditor-emailEditor-common-axisY')"
        :x-label="$_('visualEditor-emailEditor-common-axisX')"
        :centerLabel="$_('visualEditor-emailEditor-common-padding')"
        :min="0"
        :max="200"
        :step="1"
      />
      <small class="text-muted d-block mt-1">{{ $_("visualEditor-emailEditor-container-paddingHelp") }}</small>
    </div>

    <div class="form-group">
      <VeBackgroundPicker
        v-model="stripeBackgroundPickerProxy"
        :label="$_('visualEditor-emailEditor-common-background')"
        :colorLabel="$_('visualEditor-emailEditor-backgroundPicker-color')"
        :imageLabel="$_('visualEditor-emailEditor-backgroundPicker-image')"
        :chooseImageLabel="$_('visualEditor-emailEditor-backgroundPicker-choose')"
        :removeImageLabel="$_('visualEditor-emailEditor-backgroundPicker-remove')"
        :emptyImageLabel="$_('visualEditor-emailEditor-backgroundPicker-empty')"
      />
    </div>

    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-container-bodyBackground") }}</label>
      <VeColorPicker v-model="contentBackgroundProxy" />
    </div>

    <div class="form-group">
      <VeBorderSettings
        v-model="contentBorderProxy"
        :label="$_('visualEditor-emailEditor-borderSettings-label')"
      />
    </div>

    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-container-contentBorderRadius") }}</label>
      <VeCornersPicker
        v-model="contentBorderRadiusProxy"
        :options="borderRadiusOptions.map((o) => ({ value: o.value, title: $_(o.labelKey) }))"
      />
    </div>

    <div class="form-group mt-4">
      <label style="display: flex; justify-content: space-between; align-items: center;">
        <span><KvAiIcon /> {{ $_("visualEditor-emailEditor-container-personalizationTitle") }}</span>
        <KvTooltip :title="$_('visualEditor-emailEditor-container-personalizationCreditTooltip')">
          <KvChip size="small" style="">
            {{ $_("visualEditor-emailEditor-container-personalizationCreditAmount") }}
          </KvChip>
        </KvTooltip>
      </label>
      <KvTypography size="extra-small" color="muted">
        {{ $_("visualEditor-emailEditor-container-personalizationDescription") }}
      </KvTypography>
      <textarea class="form-control" v-model="personalizationInstructions" rows="10"></textarea>
    </div>
  </div>
</template>

<script>
import VeSpacingPicker from "../components/VeSpacingPicker.vue";
import VeBackgroundPicker from "../components/VeBackgroundPicker.vue";
import VeBorderSettings from "../components/VeBorderSettings.vue";
import VeColorPicker from "../components/VeColorPicker.vue";
import VeCornersPicker from "../components/VeCornersPicker.vue";
import KvTypography from "@/components/ui/kv-typography.vue";
import KvAiIcon from "@/components/ui/kv-ai-icon.vue";
import KvTooltip from "@/components/ui/kv-tooltip.vue";
export default {
  name: "VeContainerSettings",
  components: { VeSpacingPicker, VeBackgroundPicker, VeBorderSettings, VeColorPicker, VeCornersPicker, KvTypography, KvAiIcon},
  props: {
    container: { type: Object, required: true },
    doc: { type: Object, required: true },
    borderRadiusOptions: { type: Array, required: true },
  },
  computed: {
    nameProxy: {
      get() {
        return this.container?.title || "";
      },
      set(v) {
        this.$set(this.container, "title", String(v ?? ""));
      },
    },
    paddingProxy: {
      get() {
        const s = this.container?.settings || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) && x >= 0 ? Math.round(x) : 0;
        };
        return {
          top: safe(s.paddingTop),
          right: safe(s.paddingRight),
          bottom: safe(s.paddingBottom),
          left: safe(s.paddingLeft),
        };
      },
      set(next) {
        const v = next || {};
        const safe = (n) => {
          const x = Number(n);
          return Number.isFinite(x) ? Math.max(0, Math.round(x)) : 0;
        };
        if (!this.container.settings) this.$set(this.container, "settings", {});
        this.$set(this.container.settings, "paddingTop", safe(v.top));
        this.$set(this.container.settings, "paddingRight", safe(v.right));
        this.$set(this.container.settings, "paddingBottom", safe(v.bottom));
        this.$set(this.container.settings, "paddingLeft", safe(v.left));
      },
    },
    stripeBackgroundPickerProxy: {
      get() {
        const s = this.container?.settings || {};
        const mode = String(s.backgroundMode || "").toLowerCase() === "image" ? "image" : "color";
        const color = s.backgroundColor || "#ffffff";
        const imageUrl = s.backgroundImageUrl || null;
        const inferredMode = imageUrl ? "image" : mode;
        return {
          mode: inferredMode,
          color,
          imageUrl,
          sizeMode: s.backgroundSizeMode || "cover",
          sizeUnit: s.backgroundSizeUnit || "pct",
          sizePx: s.backgroundSizePx,
          sizePct: s.backgroundSizePct,
          repeat: s.backgroundRepeat || "no-repeat",
          position: s.backgroundPosition || "center center",
        };
      },
      set(next) {
        const v = next || {};
        const mode = String(v.mode || "color").toLowerCase() === "image" ? "image" : "color";
        const color = typeof v.color === "string" && v.color ? v.color : "#ffffff";
        const imageUrl = typeof v.imageUrl === "string" && v.imageUrl ? v.imageUrl : null;
        if (!this.container.settings) this.$set(this.container, "settings", {});
        this.$set(this.container.settings, "backgroundMode", mode);
        this.$set(this.container.settings, "backgroundColor", color === "#ffffff" ? null : color);
        this.$set(this.container.settings, "backgroundImageUrl", imageUrl);

        // Extra image controls (size/repeat/position)
        const sizeModeRaw = String(v.sizeMode || "").toLowerCase();
        const sizeMode = sizeModeRaw === "contain" || sizeModeRaw === "custom" ? sizeModeRaw : "cover";
        const sizeUnitRaw = String(v.sizeUnit || "").toLowerCase();
        const sizeUnit = sizeUnitRaw === "px" ? "px" : "pct";
        const px = Number(v.sizePx);
        const sizePx = Number.isFinite(px) ? Math.min(2000, Math.max(100, Math.round(px))) : 600;
        const pct = Number(v.sizePct);
        const sizePct = Number.isFinite(pct) ? Math.min(100, Math.max(0, Math.round(pct))) : 100;
        const repeatRaw = String(v.repeat || "").toLowerCase();
        const repeat =
          repeatRaw === "repeat" || repeatRaw === "repeat-x" || repeatRaw === "repeat-y"
            ? repeatRaw
            : "no-repeat";
        const position = String(v.position || "center center");

        this.$set(this.container.settings, "backgroundSizeMode", sizeMode);
        this.$set(this.container.settings, "backgroundSizeUnit", sizeUnit);
        this.$set(this.container.settings, "backgroundSizePx", sizePx);
        this.$set(this.container.settings, "backgroundSizePct", sizePct);
        this.$set(this.container.settings, "backgroundRepeat", repeat);
        this.$set(this.container.settings, "backgroundPosition", position);
      },
    },
    contentBackgroundProxy: {
      get() {
        return (
          this.container?.settings?.contentBackgroundColor ||
          this.doc?.body?.contentBackgroundColor ||
          "#ffffff"
        );
      },
      set(v) {
        const fallback = this.doc?.body?.contentBackgroundColor || "#ffffff";
        const val = v === fallback ? null : v;
        if (!this.container.settings) this.$set(this.container, "settings", {});
        this.$set(this.container.settings, "contentBackgroundColor", val);
      },
    },
    contentBorderProxy: {
      get() {
        const s = this.container?.settings || {};
        const width = Number(s.contentBorderWidth ?? 0);
        const styleRaw = String(s.contentBorderStyle || "none").toLowerCase();
        const style = styleRaw === "dashed" || styleRaw === "dotted" || styleRaw === "solid" ? styleRaw : "solid";
        const enabled = styleRaw !== "none" && Number.isFinite(width) && width > 0;
        const sides = s.contentBorderSides || {};
        return {
          enabled,
          style,
          color: s.contentBorderColor || "#e5e7eb",
          width: Number.isFinite(width) && width > 0 ? Math.min(20, Math.max(1, Math.round(width))) : 1,
          sides: {
            top: sides.top !== false,
            right: sides.right !== false,
            bottom: sides.bottom !== false,
            left: sides.left !== false,
          },
        };
      },
      set(next) {
        const v = next || {};
        const enabled = Boolean(v.enabled);
        const styleRaw = String(v.style || "solid").toLowerCase();
        const style = styleRaw === "dashed" || styleRaw === "dotted" ? styleRaw : "solid";
        const color = typeof v.color === "string" && v.color ? v.color : "#e5e7eb";
        const w = Number(v.width);
        const width = Number.isFinite(w) ? Math.min(20, Math.max(1, Math.round(w))) : 1;
        const sides = v.sides || {};
        const nextSides = {
          top: sides.top !== false,
          right: sides.right !== false,
          bottom: sides.bottom !== false,
          left: sides.left !== false,
        };

        if (!this.container.settings) this.$set(this.container, "settings", {});

        if (!enabled) {
          this.$set(this.container.settings, "contentBorderStyle", "none");
          this.$set(this.container.settings, "contentBorderWidth", 0);
          this.$set(this.container.settings, "contentBorderSides", nextSides);
          this.$set(this.container.settings, "contentBorderColor", color);
          return;
        }

        this.$set(this.container.settings, "contentBorderStyle", style);
        this.$set(this.container.settings, "contentBorderWidth", width);
        this.$set(this.container.settings, "contentBorderColor", color);
        this.$set(this.container.settings, "contentBorderSides", nextSides);
      },
    },
    contentBorderRadiusProxy: {
      get() {
        const val = this.container?.settings?.contentBorderRadius;
        // Handle both number and object (custom corners)
        if (val && typeof val === "object" && !Array.isArray(val)) {
          return val;
        }
        const n = Number(val ?? 0);
        return Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
      },
      set(v) {
        if (!this.container.settings) this.$set(this.container, "settings", {});
        // Handle both number and object (custom corners)
        if (v && typeof v === "object" && !Array.isArray(v) && v.topLeft !== undefined) {
          this.$set(this.container.settings, "contentBorderRadius", v);
        } else {
          const n = Number(v);
          const next = Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
          this.$set(this.container.settings, "contentBorderRadius", next);
        }
      },
    },
    personalizationInstructions: {
      get() {
        return this.container?.settings?.personalizationInstructions || ""
      },
      set(next) {
        if (!this.container.settings) this.$set(this.container, "settings", {});
        this.$set(this.container.settings, "personalizationInstructions", next)
      } 
    }
  },
  methods: {
  },
};
</script>


